import React, { useMemo } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { Control, Controller, FieldError, get } from 'react-hook-form'

const positiveIntegerRegex = new RegExp(/^[\d]+$/)

export function NumberField(props: {
  id?: string
  name: string
  defaultValue?: null | number
  control: Control
  error?: FieldError | null | undefined
  unit?: string
  placeholder?: string
  'data-testid'?: string
  'aria-label'?: string
  autoFocus?: boolean
  maxLength?: number
  numDecimals?: number
}) {
  const {
    id,
    name,
    control,
    unit,
    placeholder,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    defaultValue,
    autoFocus,
    maxLength,
    numDecimals,
  } = props
  const { formStateRef } = control
  const { errors, touched, isSubmitted } = formStateRef.current
  const error = props.error ?? (get(errors, name) as FieldError | null | undefined)
  const isFieldTouched = get(touched, name) || isSubmitted

  const regExp = useMemo(() => {
    if (!numDecimals) {
      return positiveIntegerRegex
    } else {
      // The number of digits allowed before the decimal point.
      const numBefore = maxLength ? maxLength - numDecimals - 1 : ''

      return new RegExp(`^\\d{0,${numBefore}}(?:\\.\\d{0,${numDecimals}})?$`)
    }
  }, [numDecimals, maxLength])

  return (
    <InputGroup>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ onChange, onBlur, value }) => {
          const valueAsString =
            value === null ? '' : typeof value === 'number' ? value.toFixed(numDecimals) : value

          return (
            <Form.Control
              id={id}
              autoFocus={autoFocus}
              type="text"
              name={name}
              isInvalid={Boolean(isFieldTouched) && Boolean(error)}
              placeholder={placeholder || ''}
              onChange={(e) => {
                // when user is typing, we only want to cleanup the value so user can not enter invalid chars,
                // and trigger onChange with a string version of the value, no attempt to convert to number here...
                // because if we convert the number here, it's quite tricky to let user enter something like "-0.01" to the input
                //
                // the conversion to number will happen on onBlur
                //
                const newValue = e.target.value.trim()
                // Analyze each character in the new value one at a time, to support
                // pasting and other sudden changes in the input
                const cleanVal = newValue
                  .split('')
                  .reduce((acc, ch) => (regExp.test(acc + ch) ? acc + ch : acc), '')

                if (cleanVal !== valueAsString) {
                  onChange(cleanVal || null)
                }
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                const val = e.target.value.trim()
                const numberValue = parseFloat(val)
                if (isNaN(numberValue)) {
                  onChange(null)
                } else {
                  onChange(numberValue)
                }
                onBlur()
              }}
              value={valueAsString}
              data-testid={dataTestId}
              aria-label={ariaLabel}
              inputMode="numeric"
              // For decimal inputs, maxLength is enforced by a RegExp instead.
              maxLength={numDecimals ? undefined : maxLength}
            />
          )
        }}
      />
      {unit && (
        <InputGroup.Append>
          <InputGroup.Text>{unit}</InputGroup.Text>
        </InputGroup.Append>
      )}
      {isFieldTouched && error && (
        <Form.Control.Feedback type="invalid">{error.message}</Form.Control.Feedback>
      )}
    </InputGroup>
  )
}
